<script setup lang="ts">
import ProjectService from '@/core/shared/project/project.service';
import ProjectCard from '@/components/Shared/Projects/ProjectCard/ProjectCard.vue';
import { computed } from 'vue';
import TrackingService from '@/core/shared/tracking/tracking.service';
import { TrackingActionName } from '@/core/shared/tracking/tracking-actions';
import SpinnerLoader from '@/components/Shared/Loaders/SpinnerLoader.vue';
import ProjectLoaderCard from '@/components/Shared/Projects/ProjectLoaderCard.vue';
import ProjectEmptyState from '@/components/Shared/Projects/ProjectEmptyState.vue';

const props = defineProps<{
  projectId?: number;
  /** The job a click would not move the route off; undefined when every click should navigate. */
  targetProjectId?: number;
}>();

const emit = defineEmits<{
  (e: 'change:project-id', projectId: number): void;
  (e: 'add-job'): void;
}>();

const projectService = new ProjectService();

const projects = computed(() => projectService.projects);

const isSearchingProjects = computed(() => {
  return projectService.isSearchingProjects;
});

const isSearchingMoreProjects = computed(() => {
  return projectService.isSearchingMoreProjects;
});

const hasProjects = computed(() => {
  return projects.value.length > 0;
});

// The empty state fills the list height, so the infinite-scroll sentinel below it has to share that
// height (flex column) instead of stacking under a full-height block and overflowing the scroller.
const isShowingEmptyState = computed(() => !isSearchingProjects.value && !hasProjects.value);

const hasFilters = computed(() => {
  return projectService.filtersCount > 0 || projectService.titleOrReqId || projectService.copilot;
});

async function changeProject(projectId: number) {
  if (props.targetProjectId === projectId) {
    return;
  }

  TrackingService.trackAction(TrackingActionName.PROJECT_CARD_CLICKED, { source: 'Sourcing' });
  emit('change:project-id', projectId);
}

function addJob() {
  emit('add-job');
}
</script>

<template>
  <div class="h-full" :class="{ 'flex flex-col': isShowingEmptyState }" data-test="project-list">
    <template v-if="!isSearchingProjects">
      <template v-if="hasProjects">
        <ProjectCard
          v-for="project in projects"
          tabindex="0"
          :key="project.id"
          :id="`project-card-${project.id}`"
          :project="project"
          :is-card-selected="project.id === projectId"
          @click="changeProject(project.id)"
        />
      </template>
      <ProjectEmptyState
        v-else
        :type="hasFilters ? 'no-results' : 'empty-my-jobs'"
        @add-job="addJob"
      />
    </template>

    <template v-else>
      <ProjectLoaderCard v-for="i in 4" :key="i" />
    </template>

    <!-- Keep this element with a fixed height (h-8) so that the infinite scroll observer works
     If it has a height of 0px, the observer will not work -->
    <div id="project-infinite-scroll" class="mt-8 flex h-8 shrink-0 items-center justify-center">
      <SpinnerLoader v-show="isSearchingMoreProjects" />
    </div>
  </div>
</template>
